import NextIcon from '@src/assets/images/common/icon/files/next-icon.svg';
import PrevIcon from '@src/assets/images/common/icon/files/prev-icon.svg';
import { COMMUNITY_IMAGE_REGEX } from '@src/constants/fileTypes';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { Attachment } from '@src/utils/idb';
import React, { useEffect, useRef, useState } from 'react';
import { useLongPress } from 'use-long-press';

import AttachmentDeleteMenuSp from './AttachmentDeleteMenuSp';
import PreviewFileItem from './PreviewFileItem';
import PreviewImageItem from './PreviewImageItem';

export type PreviewFileListProps = {
  attachments?: Attachment[];
  upload?: {
    enabled: boolean;
    onDelete: (attachment: Attachment) => void;
  };
  onImageItemClick?: (attachment: Attachment) => void;
};

const PreviewFileList: React.FC<PreviewFileListProps> = ({
  attachments = [],
  upload,
  onImageItemClick,
}) => {
  const isUploadMode = upload?.enabled === true;
  const handleDelete = (attachment: Attachment) => {
    if (isUploadMode && upload) {
      upload.onDelete(attachment);
    }
  };
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const SCROLL_AMOUNT = 300;

  const isMobile = useIsMobile();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isSpDeleteOpen, setIsSpDeleteOpen] = useState(false);
  const [targetIndex, setTargetIndex] = useState<number | null>(null);

  const isUploaded = (att: Attachment): boolean =>
    typeof att.chatAttachment?.file_url === 'string' && att.chatAttachment.file_url.length > 0;

  useEffect(() => {
    if (!isSpDeleteOpen) {
      setSelectedIndex(null);
    }
  }, [isSpDeleteOpen]);

  const handleTapItem = (idx: number, attachment: Attachment, isImage?: boolean) => {
    if (!isMobile) {
      return;
    }

    const imageCheck = isImage ?? COMMUNITY_IMAGE_REGEX.test(attachment.name);
    if (imageCheck && onImageItemClick) {
      onImageItemClick(attachment);
      return;
    }

    setSelectedIndex((prev) => (prev === idx ? null : idx));
  };

  const longPressBind = useLongPress(
    (_e, { context }) => {
      const idx = Number(context);
      if (!isMobile) {
        return;
      }
      const att = attachments[idx];
      if (!isUploaded(att)) {
        return;
      }

      setSelectedIndex(null);
      setTargetIndex(idx);
      setIsSpDeleteOpen(true);
    },
    { threshold: 650, cancelOnMovement: 10 },
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const updateButtons = () => {
      setShowLeftButton(container.scrollLeft > 0);
      setShowRightButton(container.scrollLeft < container.scrollWidth - container.clientWidth);
    };
    container.addEventListener('scroll', updateButtons);
    updateButtons();
    return () => container.removeEventListener('scroll', updateButtons);
  }, [attachments]);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div
      className='relative overflow-hidden'
      style={{ height: '64px' }}
    >
      <div
        ref={scrollContainerRef}
        className='flex gap-2 overflow-x-auto px-2'
        style={{ height: '200%' }}
      >
        {attachments.map((attachment, index) => {
          const isImage = COMMUNITY_IMAGE_REGEX.test(attachment.name);
          const key = `${attachment.name}-${attachment.size}-${index}`;
          const selected = selectedIndex === index;
          const isMenuTarget = targetIndex === index && isSpDeleteOpen;

          const commonProps = isMobile
            ? {
                ...(isImage ? {} : { onClick: () => handleTapItem(index, attachment, isImage) }),
                onContextMenu: (e: React.MouseEvent) => {
                  e.preventDefault();
                },
                className: `${isImage ? 'size-14' : 'h-14'} flex-shrink-0 select-none ${
                  isImage ? 'touch-none' : 'touch-manipulation'
                }`,
                ...(isUploadMode ? longPressBind(index) : {}),
              }
            : {
                className: `${isImage ? 'size-14' : 'h-14'} flex-shrink-0`,
              };

          return isImage ? (
            <div
              key={key}
              {...commonProps}
            >
              <PreviewImageItem
                attachment={attachment}
                upload={isUploadMode}
                onDelete={() => handleDelete(attachment)}
                onItemClick={onImageItemClick ? () => onImageItemClick(attachment) : undefined}
                selected={isMobile ? isMenuTarget : selected}
              />
            </div>
          ) : (
            <div
              key={key}
              {...commonProps}
            >
              <PreviewFileItem
                attachment={attachment}
                upload={isUploadMode}
                onDelete={() => handleDelete(attachment)}
              />
            </div>
          );
        })}
      </div>
      <button
        onClick={() =>
          scrollContainerRef.current?.scrollBy({
            left: -SCROLL_AMOUNT,
            behavior: 'smooth',
          })
        }
        className={`absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white shadow transition-opacity duration-300 ${
          showLeftButton ? 'opacity-100' : 'opacity-0'
        }`}
        aria-label='前へ'
      >
        <img
          src={PrevIcon}
          alt='前へ'
          width={24}
          height={24}
          className='object-contain'
        />
      </button>

      <button
        onClick={() =>
          scrollContainerRef.current?.scrollBy({
            left: SCROLL_AMOUNT,
            behavior: 'smooth',
          })
        }
        className={`absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white shadow transition-opacity duration-300 ${
          showRightButton ? 'opacity-100' : 'opacity-0'
        }`}
        aria-label='次へ'
      >
        <img
          src={NextIcon}
          alt='次へ'
          width={24}
          height={24}
          className='object-contain'
        />
      </button>
      {isSpDeleteOpen && targetIndex != null && (
        <AttachmentDeleteMenuSp
          onClose={() => {
            setIsSpDeleteOpen(false);
            setTargetIndex(null);
          }}
          onDelete={() => {
            handleDelete(attachments[targetIndex]); // 既存の削除ハンドラを呼ぶ
          }}
        />
      )}
    </div>
  );
};

export default PreviewFileList;
